import { ImageScaleView, ImageScaleViewOpts } from '@play-co/timestep-core/ui';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { Match2GoalItem } from './Match2GoalItem';
import { GoalsDef } from '../game/defs/map';
import { GoalId } from '../game/defs/goal';
import StateObserver, { user } from 'src/StateObserver';
import { consolidateGoals } from '../match2.util';
import features from 'src/replicant/ruleset/features';

type Match2GoalsPanelOpts = Omit<
  ImageScaleViewOpts,
  'image' | 'width' | 'height'
>;

export class Match2GoalsPanelNew extends ImageScaleView {
  private goalsContainer: FlexBoxView;
  private items: Match2GoalItem[] = [];

  constructor(opts: Match2GoalsPanelOpts = {}) {
    const width = 216;
    const height = 184;
    super({
      image: 'assets/ui/match2/level/panel_goals.png',
      width,
      height,
      ...opts,
    });

    const locale = StateObserver.getLocale();
    const titleWidth = 200;
    const titleHeight = 50;

    new ImageScaleView({
      parent: this,
      width: titleWidth,
      height: titleHeight,
      image: `assets/ui/match2/level/goals_title_${locale}.png`,
      scaleMethod: 'contain',
      x: width * 0.5,
      y: 5,
      centerOnOrigin: true,
    });

    this.goalsContainer = new FlexBoxView({
      superview: this,
      width: 200,
      height: 140,
      flex: {
        flexDirection: 'row',
        alignContent: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
      },
      y: 10,
      x: 3,
      canHandleEvents: false,
      zIndex: 1,
    });
  }

  public setGoals(goals: GoalsDef) {
    // If the new first 2 obstacle visuals feature is enabled, we need to determine if we should use the crate icon
    // for non-consolidated hatAll goals - since colored hats and parcels obstacles are consolidated
    // down into the hatAll that requires the original icon to be used.
    const useCrateIcon =
      features(user()).newFirst2ObstacleVisuals && goals.hatAll;

    goals = consolidateGoals(goals);
    this.goalsContainer.removeAllSubviews();

    const goalEntries = Object.entries(goals);
    for (const [key, value] of goalEntries) {
      let item = this.items.find((item) => item.getParents().length === 0);

      if (!item) {
        const baseOpts = { countFontSize: 25, countOffsetX: -5 };
        const opts = goalEntries.length > 3 ? { width: 30, height: 30 } : {};
        item = new Match2GoalItem({ ...baseOpts, ...opts });
        const flex = getFlexNode(item);
        flex.marginHorizontal = [5, 6].includes(goalEntries.length) ? 10 : 9;
        flex.marginVertical = 3;
        this.items.push(item);
      }

      item.setGoal(
        key as GoalId,
        value,
        // Force apply crate icon for non-consolidated hatAll goals
        useCrateIcon && key === 'hatAll' ? 'icon.crate.png' : undefined,
      );
      this.goalsContainer.addSubview(item);
    }

    this.updateGoalsVisibility(goals);
  }

  public getGoal(id: GoalId) {
    return this.items.find((item) => item.goalId === id);
  }

  private updateGoalsVisibility(goals: GoalsDef) {
    const entries = Object.entries(goals);

    // Sort items by the highest goal count
    const sortedEntries = entries.sort((a, b) => b[1] - a[1]);

    // This panel can only show 6 goals at a time
    const maxVisibleItems = 8;

    // Count the number of visible items
    let numVisibleItems = 0;

    for (const [key] of sortedEntries) {
      const item = this.getGoal(key as GoalId);
      if (!item) continue;

      // Show the item if it's one of the top 8 goals sorted by count
      if (numVisibleItems < maxVisibleItems) {
        numVisibleItems += 1;
        item.style.visible = true;
      } else {
        item.style.visible = false;
      }
    }
  }
}
